
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoPanel, IOnOffHandlers } from 'bonomo/dist';
import LockerNav from '@/components/locker/widget/LockerNav.vue';
import { ILocker2 } from '@/model/Locker/lockerInterfaces';
import { IShortCommunity } from '@/model/Community/communityInterfaces';
import { IShortUser } from '@/model/shortInterfaces';
import { ILockerCompany } from '@/model/LockerCompany/lockerCompanyInterface';
import { IZone } from '@/store/modules/settings/zonesModule';
import { IErrors } from '@/model/IErrors';
import { EPermissionActions } from '@/model/EPermissionActions';
import { EUserPermissions } from '@/model/EUserPermissions';
import { PermissionHelper } from '@/helpers/PermissionHelper';
import { IAuthUserData } from '@/store/modules/authModule';

import { uiBus } from '@/plugins/uiBus';
import { communityService } from '@/services/communityService';

const locker2Module = namespace('locker2Module');
const authModule = namespace('authModule');
const lockerCompanyModule = namespace('lockerCompanyModule');
const zonesModule = namespace('zonesModule');
const userModule = namespace('userModule');

@Component({
  name: 'LockerEdit',
  components: { BoPanel, LockerNav },
})
export default class LockerEdit extends Vue {

  @Prop()
  protected lockerId!: number;

  @locker2Module.Getter('locker')
  protected locker!: ILocker2;

  @locker2Module.Getter('errors')
  protected errors!: IErrors;

  @locker2Module.Action('fetchLocker2')
  private fetchLocker2!: (lockerId: number) => Promise<any>;

  @locker2Module.Action('updateLocker')
  private updateLocker!: (params: { lockerId: number; data: any }) => Promise<any>;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  @lockerCompanyModule.Getter('lockerCompanies')
  protected lockerCompanies!: ILockerCompany[];

  @lockerCompanyModule.Action('fetchLockerCompanies')
  private fetchLockerCompanies!: (filters?: any) => Promise<any>;

  @lockerCompanyModule.Action('connectLockerCompany')
  private connectLockerCompany!: (params: { lockerCompanyId: number; lockerId: number; connect: boolean }) => Promise<any>;

  @zonesModule.Getter('zones')
  protected zones!: IZone[];

  @zonesModule.Action('fetchZones')
  private fetchZones!: () => Promise<any>;

  @userModule.Getter('lockerOwners')
  protected lockerOwners!: IShortUser[];

  @userModule.Action('fetchLockerOwners')
  private fetchLockerOwners!: () => Promise<any>;

  protected loading: boolean = true;
  protected saving: boolean = false;
  private subscribes: IOnOffHandlers = {};
  private form: any = {};
  private allCommunities: IShortCommunity[] = [];
  private communitiesLoading: boolean = false;


  readonly availableLanguages = ['es', 'ca', 'en', 'fr', 'pt', 'dk', 'de'];

  get isAdmin(): boolean {
    return !!this.authUser && PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_ADMINISTRATOR);
  }

  get canEdit(): boolean {
    return this.$canAction(EPermissionActions.ACTION_LOCKERS_UPDATE);
  }

  get channelItems(): { text: string; value: number }[] {
    const channels = this.$t('channels.names') as any;
    const ret: { text: string; value: number }[] = [];
    for (let i in Object.keys(channels)) {
      ret.push({ text: channels[i], value: Number(i) });
    }
    return ret;
  }

  get communityItems(): { text: string; value: number }[] {
    const items = this.allCommunities.map((c) => ({ text: c.name, value: c.id }));
    if (this.locker && this.locker.community_id) {
      const currentId = this.locker.community_id;
      const found = items.find((i) => i.value === currentId);
      if (!found) {
        items.unshift({ text: this.locker.community_name || ('ID ' + currentId), value: currentId });
      }
    }
    return items;
  }

  get lockerCompanyItems(): { text: string; value: number }[] {
    return (this.lockerCompanies || []).map(c => ({ text: c.description, value: c.id }));
  }

  get zoneItems(): { text: string; value: number }[] {
    return (this.zones || []).map(z => ({ text: z.name, value: z.id }));
  }

  get statusOptions() {
    return [
      { text: 'Install', value: 'Install' },
      { text: 'Online', value: 'Online' },
      { text: 'Off', value: 'Off' },
      { text: 'Deleted', value: 'Deleted' },
    ];
  }

  get internalDeliveryOptions() {
    return [
      { text: 'Off', value: 0 },
      { text: 'Bayer', value: 1 },
    ];
  }

  private rules = {
    required: (v: any) => (v !== null && v !== undefined && v !== '') || this.$t('common.validation.required'),
    min2: (v: string) => (!!v && v.length >= 2) || this.$t('common.validation.min', { min: 2 }),
  };

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.locker2Loaded': this.initForm,
      'backend.call.begin.locker2Loaded': () => { this.saving = false; },
    });
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  loadData() {
    this.fetchLocker2(this.lockerId);
    this.fetchAllCommunitiesLocal();
    if (!this.lockerCompanies || !this.lockerCompanies.length) {
      this.fetchLockerCompanies();
    }
    if (!this.zones || !this.zones.length) {
      this.fetchZones();
    }
    if (!this.lockerOwners || !this.lockerOwners.length) {
      this.fetchLockerOwners();
    }
  }

  private async fetchAllCommunitiesLocal() {
    this.communitiesLoading = true;
    try {
      this.allCommunities = await communityService.fetchAll();
    } catch (e) {
      // fallback: el item de la community actual sigue visible via communityItems getter
    } finally {
      this.communitiesLoading = false;
    }
  }

  private initForm() {
    if (!this.locker || !this.locker.id) return;
    this.form = {
      name: this.locker.name,
      abbreviation: this.locker.abbreviation,
      description: this.locker.description,
      location_description: this.locker.location_description,
      location_building_name: this.locker.location_building_name,
      location_building_floor: this.locker.location_building_floor,
      default_phone_prefix: this.locker.default_phone_prefix,
      should_send_reminders: this.locker.should_send_reminders,
      languages: this.locker.languages ? [...this.locker.languages] : [],
      community_id: this.locker.community_id || null,
      channel_id: this.locker.channel_id || null,
      owner_user_id: this.locker.owner_user_id || null,
      zone_id: this.locker.zone_id || null,
      lockerCompanyIds: this.locker.lockerCompanyIds ? [...this.locker.lockerCompanyIds] : [],
      status: this.locker.status,
      internal_delivery_type: this.locker.internal_delivery_type,
    };
  }

  ownerLabel(user: IShortUser): string {
    return user.first_name + ' ' + user.last_name + ' <' + user.email + '>';
  }

  private async onSave() {
    const formRef = this.$refs.editForm as any;
    if (formRef && !formRef.validate()) return;

    this.saving = true;
    try {
      const originalCompanyIds: number[] = this.locker.lockerCompanyIds ? [...this.locker.lockerCompanyIds] : [];
      const newCompanyIds: number[] = this.form.lockerCompanyIds || [];

      const { lockerCompanyIds, ...lockerData } = this.form;
      await this.updateLocker({ lockerId: this.lockerId, data: lockerData });

      const toAdd = newCompanyIds.filter((id: number) => originalCompanyIds.indexOf(id) === -1);
      const toRemove = originalCompanyIds.filter((id: number) => newCompanyIds.indexOf(id) === -1);
      await Promise.all([
        ...toAdd.map((id: number) => this.connectLockerCompany({ lockerCompanyId: id, lockerId: this.lockerId, connect: true })),
        ...toRemove.map((id: number) => this.connectLockerCompany({ lockerCompanyId: id, lockerId: this.lockerId, connect: false })),
      ]);

      uiBus.emit('success', this.$t('common.saved'));
      this.$router.push({ name: 'locker', params: { lockerId: '' + this.lockerId } });
    } finally {
      this.saving = false;
    }
  }

  private onCancel() {
    this.$router.push({ name: 'locker', params: { lockerId: '' + this.lockerId } });
  }
}
